import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  CROP_KEY_PREFIX,
  FARM_KEY_PREFIX,
  INPUT_KEY_PREFIX,
  ORG_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import { ZoneLinkType } from 'interfaces/IInput';
import type { IInput } from 'interfaces/IInput';
import { filterInputsByYear, groupInputsByCrop } from 'utils/FarmUtils';

import { RootState } from '../redux/Store';

import { useVisibleOrgData } from 'hooks/useVisibleOrgData';

const EMPTY_INPUTS: IInput[] = [];

/**
 * Returns the subset of PlanActual inputs for the active organisation that
 * falls within the currently selected tree node scope (org / farm / paddock /
 * input).
 *
 * Mirrors the `scopedInputs` memo in OrganisationWorkspace without the draft
 * substitution — this hook reads only server state (React Query cache).
 * Both hooks share the same React Query cache key so no additional network
 * requests are issued.
 */
export const useScopedInputs = (): {
  scopedInputs: IInput[];
  /** Every PlanActual input in the active org, unscoped and unfiltered by year — for wider-scope context (OP-2392). */
  orgInputs: IInput[];
  isLoading: boolean;
} => {
  const { organisationId: activeOrganisationId, isSwitchingOrganisation } =
    useSelector((state: RootState) => state.activeOrganisationReducer);
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const treeYearFilter = useSelector(
    (state: RootState) => state.farmReducer.treeYearFilter
  );

  // Excluded farms/paddocks and their plans are hidden everywhere (OP-2390).
  const {
    farms,
    inputs,
    farmsQuery: { isLoading: isFarmsLoading },
    inputsQuery: { isLoading: isInputsLoading },
  } = useVisibleOrgData(activeOrganisationId);

  const scopedInputs = useMemo<IInput[]>(() => {
    // For aggregate scopes (org / farm / paddock / crop) apply the same year
    // filter the tree uses, so panel totals only cover visible plans.
    // For an INPUT node the user has explicitly opened that document — no year
    // filtering needed (mirrors OrganisationWorkspace.scopedInputs logic).
    const yearFilteredInputs = filterInputsByYear(inputs, treeYearFilter);

    if (!activeTreeNodeKey) return yearFilteredInputs;

    if (activeTreeNodeKey.startsWith(INPUT_KEY_PREFIX)) {
      const inputId = activeTreeNodeKey.replace(INPUT_KEY_PREFIX, '');
      const found = inputs.find((i) => i.id === inputId);
      return found ? [found] : [];
    }

    if (activeTreeNodeKey.startsWith(PADDOCK_KEY_PREFIX)) {
      const paddockId = activeTreeNodeKey.replace(PADDOCK_KEY_PREFIX, '');
      return yearFilteredInputs.filter((input) =>
        input.zone?.links?.some(
          (l) => l.linkType === ZoneLinkType.Paddock && l.linkId === paddockId
        )
      );
    }

    if (activeTreeNodeKey.startsWith(FARM_KEY_PREFIX)) {
      const farmId = activeTreeNodeKey.replace(FARM_KEY_PREFIX, '');
      const farm = farms.find((f) => f.id === farmId);
      if (!farm) return [];
      const paddockIds = new Set(farm.paddocks.map((p) => p.paddockId));
      return yearFilteredInputs.filter((input) =>
        input.zone?.links?.some(
          (l) => l.linkType === ZoneLinkType.Paddock && paddockIds.has(l.linkId)
        )
      );
    }

    if (activeTreeNodeKey.startsWith(ORG_KEY_PREFIX)) {
      return yearFilteredInputs;
    }

    if (activeTreeNodeKey.startsWith(CROP_KEY_PREFIX)) {
      const cropKey = activeTreeNodeKey.replace(CROP_KEY_PREFIX, '');
      return (
        groupInputsByCrop(yearFilteredInputs).find(
          (group) => group.cropKey === cropKey
        )?.inputs ?? []
      );
    }

    return [];
  }, [activeTreeNodeKey, inputs, farms, treeYearFilter]);

  return {
    // Mirror OrganisationWorkspace: blank the panel immediately when the org
    // switch begins, before activeOrganisationId actually changes.
    scopedInputs: isSwitchingOrganisation ? [] : scopedInputs,
    orgInputs: isSwitchingOrganisation ? EMPTY_INPUTS : inputs,
    isLoading: isSwitchingOrganisation || isInputsLoading || isFarmsLoading,
  };
};
